import { Cross2Icon, ReloadIcon } from '@radix-ui/react-icons';
import { useEffect, useId, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { TimedGameSnapshot } from '@wx-weiqi/contracts';
import type { TeachingStore } from '../stores/teaching-store';
import './teacher-observation-game-picker.css';

const CURRENT_GAME_STATUSES = [
  'CREATED',
  'WAITING',
  'READY',
  'PLAYING',
  'COUNTING_REQUESTED',
  'SCORING',
] as const;

const STATUS_LABELS: Readonly<Record<TimedGameSnapshot['status'], string>> = {
  CREATED: '已创建',
  WAITING: '等待对手',
  READY: '准备开始',
  PLAYING: '对局中',
  COUNTING_REQUESTED: '申请数子',
  SCORING: '数子中',
  FINISHED: '已结束',
  ABORTED: '已中止',
};

export function TeacherObservationGamePicker({
  teaching,
  currentGameId,
  onSelect,
  onClose,
}: {
  readonly teaching: Pick<TeachingStore, 'subscribe' | 'snapshot' | 'loadTeacherGames'>;
  readonly currentGameId: string;
  readonly onSelect: (gameId: string) => void;
  readonly onClose: () => void;
}) {
  const state = useSyncExternalStore(teaching.subscribe, teaching.snapshot, teaching.snapshot);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const [classId, setClassId] = useState(state.teacherGameFilters.classId ?? '');
  const [revision, setRevision] = useState(0);
  const pending = state.pending === 'load-teacher-games';
  const games = state.teacherGames.filter(
    (game) => game.status !== 'FINISHED' && game.status !== 'ABORTED',
  );

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    const previous = document.activeElement;
    dialog?.showModal();
    closeRef.current?.focus();
    return () => {
      dialog?.close();
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    void teaching
      .loadTeacherGames({
        status: CURRENT_GAME_STATUSES,
        ...(classId === '' ? {} : { classId }),
      })
      .catch(() => undefined);
  }, [teaching, classId, revision]);

  return (
    <dialog
      ref={dialogRef}
      className="teacher-observation-game-picker"
      aria-labelledby={titleId}
      onKeyDown={(event) => event.stopPropagation()}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
    >
      <header className="teacher-observation-game-picker-heading">
        <h2 id={titleId}>切换对局</h2>
        <button ref={closeRef} type="button" aria-label="关闭对局列表" onClick={onClose}>
          <Cross2Icon aria-hidden="true" />
        </button>
      </header>
      <div className="teacher-observation-game-picker-filters">
        <label>
          <span>班级</span>
          <select
            value={classId}
            disabled={pending}
            onChange={(event) => setClassId(event.currentTarget.value)}
          >
            <option value="">全部班级</option>
            {state.classes.map((managedClass) => (
              <option key={managedClass.classId} value={managedClass.classId}>
                {managedClass.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" disabled={pending} onClick={() => setRevision((value) => value + 1)}>
          <ReloadIcon aria-hidden="true" />
          {pending ? '刷新中…' : '刷新'}
        </button>
      </div>
      <div className="teacher-observation-game-picker-body" aria-busy={pending}>
        {pending ? (
          <p className="teacher-observation-game-picker-message" role="status">
            正在加载学生对局…
          </p>
        ) : state.error !== null ? (
          <div className="teacher-observation-game-picker-message" role="alert">
            <p>{state.error}</p>
            <button type="button" onClick={() => setRevision((value) => value + 1)}>
              重新加载
            </button>
          </div>
        ) : games.length === 0 ? (
          <p className="teacher-observation-game-picker-message" role="status">
            当前班级暂无进行中的对局。
          </p>
        ) : (
          <ul className="teacher-observation-game-picker-list" aria-label="学生对局列表">
            {games.map((game) => {
              const black = game.players.find((player) => player.seat === 'BLACK');
              const white = game.players.find((player) => player.seat === 'WHITE');
              const current = game.gameId === currentGameId;
              return (
                <li key={game.gameId} aria-current={current ? 'true' : undefined}>
                  <div className="teacher-observation-game-picker-players">
                    <span>
                      <i className="teacher-observation-game-picker-stone" aria-hidden="true" />
                      <small>黑方</small>
                      <strong>{black?.displayName ?? '等待加入'}</strong>
                    </span>
                    <span>
                      <i
                        className="teacher-observation-game-picker-stone teacher-observation-game-picker-stone--white"
                        aria-hidden="true"
                      />
                      <small>白方</small>
                      <strong>{white?.displayName ?? '等待加入'}</strong>
                    </span>
                  </div>
                  <div className="teacher-observation-game-picker-progress">
                    <strong>{STATUS_LABELS[game.status]}</strong>
                    <span>
                      {game.boardSize} 路 · 第 {game.currentSequence} 手
                    </span>
                  </div>
                  <button
                    type="button"
                    disabled={current}
                    aria-label={
                      current
                        ? '正在观看当前对局'
                        : `观看${black?.displayName ?? '待加入黑方'}与${white?.displayName ?? '待加入白方'}的对局`
                    }
                    onClick={() => onSelect(game.gameId)}
                  >
                    {current ? '正在观看' : '观看此局'}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <footer>
        <button type="button" onClick={onClose}>
          返回当前对局
        </button>
      </footer>
    </dialog>
  );
}
